import React, { useState, useEffect } from 'react';
import {
  Heart,
  MessageCircle,
  Share,
  MoreHorizontal,
  Plus,
  X,
  Send,
  Image,
  Video,
  MapPin,
  Trophy,
  Award,
  Users
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import CommentsSection from '../../components/common/CommentsSection';
import StoriesSection from '../../components/common/StoriesSection';
import StoryViewer from '../../components/common/StoryViewer';
import StoryCreator from '../../components/common/StoryCreator';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PostAnalytics from '../../components/common/PostAnalytics';
import ReportModal from '../../components/common/ReportModal';
import ShareModal from '../../components/common/ShareModal';
import ModerationNotifications from '../../components/user/ModerationNotifications';
import { StoryGroup } from '../../types/story';
import api from '../../services/api';
import { uploadService } from '../../services/api';
import { normalizeSportName, getSportIcon } from '../../utils/sportUtils';


// Mock data
const mockStories = [
  { id: 'you', name: 'You', avatar: null, isAdd: true },
  { id: '1', name: 'Meera', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=60&h=60&fit=crop&crop=face' },
  { id: '2', name: 'Kavya', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=60&h=60&fit=crop&crop=face' },
  { id: '3', name: 'Madhur', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face' },
  { id: '4', name: 'Shweta', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face' }
];

const mockPosts = [
  {
    id: '6910690fd448faf4ad570d4c', // Real post ID from database
    user: {
      name: 'Anuj Kumar',
      role: 'Player',
      sport: 'Football',
      avatar: null as string | null
    },
    timeAgo: '2h',
    content: 'Just had an amazing football training session! Feeling stronger and more confident with each practice. 💪⚽',
    image: 'https://images.unsplash.com/photo-1606166187734-a4cb74079037?w=400&h=300&fit=crop' as string | null,
    likes: 125,
    comments: 20,
    shares: 5,
    isLiked: false
  },
  {
    id: '6910690fd448faf4ad570d4d', // Real post ID from database
    user: {
      name: 'Anuj Kumar',
      role: 'Player',
      sport: 'Football',
      avatar: null as string | null
    },
    timeAgo: '4h',
    content: 'Excited to share my progress in football! The dedication is paying off and I can see improvement in my game.',
    image: null as string | null,
    likes: 89,
    comments: 15,
    shares: 8,
    isLiked: true
  },
  {
    id: '69106910d448faf4ad570d5e', // Real post ID from database
    user: {
      name: 'Meera Sharma',
      role: 'Player',
      sport: 'Chess',
      avatar: null as string | null
    },
    timeAgo: '6h',
    content: 'Just had an amazing chess training session! Feeling more strategic and focused with each game. ♟️🧠',
    image: 'https://images.unsplash.com/photo-1528819622765-d6bcf132f793?w=400&h=300&fit=crop' as string | null,
    likes: 67,
    comments: 23,
    shares: 18,
    isLiked: false
  }
];

// Feed Achievement Card with Flip Animation and Image Support
const FeedAchievementCard: React.FC<{ achievement: any; image?: string | null }> = ({ achievement, image }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  const imageUrl = image || null;

  return (
    <div
      className="relative h-52 cursor-pointer group"
      onClick={(e) => { e.stopPropagation(); setIsFlipped(!isFlipped); }}
      style={{ perspective: '1000px' }}
    >
      <div
        className="relative w-full h-full transition-transform duration-700"
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        {/* Front Side */}
        <div
          className={`absolute inset-0 w-full h-full rounded-xl shadow-lg overflow-hidden ${!imageUrl ? 'bg-gradient-to-br from-yellow-400 via-amber-500 to-yellow-600' : ''}`}
          style={{ backfaceVisibility: 'hidden' }}
        >
          {imageUrl ? (
            <>
              <img src={imageUrl} alt={achievement.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                <div className="flex items-center space-x-2 mb-1">
                  <Trophy className="w-4 h-4 text-yellow-300" />
                  <span className="font-bold text-sm sm:text-base">{achievement.title}</span>
                  {achievement.verified && (
                    <span className="px-2 py-0.5 bg-green-500/80 text-white text-xs font-semibold rounded-full">✓</span>
                  )}
                </div>
                {achievement.sport && (
                  <span className="px-2 py-0.5 bg-yellow-500/60 text-white text-xs rounded-full font-medium capitalize">{achievement.sport}</span>
                )}
                <p className="text-xs text-white/70 mt-2 animate-pulse">Tap to see details →</p>
              </div>
            </>
          ) : (
            <div className="relative z-10 flex flex-col items-center justify-center h-full text-center p-4 text-white">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full -mr-12 -mt-12"></div>
              <div className="p-3 bg-white/20 rounded-full mb-3">
                <Trophy className="w-8 h-8 text-white" />
              </div>
              <span className="font-bold text-base sm:text-lg mb-1">{achievement.title}</span>
              {achievement.verified && (
                <span className="px-2 py-0.5 bg-green-500/80 text-white text-xs font-semibold rounded-full mb-1">✓ Verified</span>
              )}
              {achievement.sport && (
                <span className="px-2 py-0.5 bg-yellow-700/60 text-white text-xs rounded-full font-medium capitalize">{achievement.sport}</span>
              )}
              <p className="text-xs text-white/70 mt-2 animate-pulse">Tap to see details →</p>
            </div>
          )}
        </div>

        {/* Back Side */}
        <div
          className="absolute inset-0 w-full h-full p-5 bg-gradient-to-br from-yellow-50 via-amber-50 to-yellow-100 border-2 border-yellow-200 rounded-xl shadow-lg"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center space-x-2 mb-2">
              <div className="p-2 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full">
                <Trophy className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-gray-900 text-sm sm:text-base">{achievement.title}</span>
              {achievement.verified && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">✓</span>
              )}
            </div>
            {achievement.description && (
              <p className="text-sm text-gray-600 mb-2 flex-1">{achievement.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 border-t border-yellow-200 pt-2">
              {achievement.sport && (
                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full font-medium capitalize">{achievement.sport}</span>
              )}
              {achievement.date && (
                <span>📅 {new Date(achievement.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
              )}
              {!achievement.date && achievement.year && (
                <span>📅 {achievement.year}</span>
              )}
            </div>
            <p className="text-xs text-amber-600 text-center mt-2 animate-pulse">Tap to flip back ←</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Feed Certificate Card with Flip Animation and Image Support
const FeedCertificateCard: React.FC<{ certificate: any; image?: string | null }> = ({ certificate, image }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  const imageUrl = image || null;

  return (
    <div
      className="relative h-52 cursor-pointer group"
      onClick={(e) => { e.stopPropagation(); setIsFlipped(!isFlipped); }}
      style={{ perspective: '1000px' }}
    >
      <div
        className="relative w-full h-full transition-transform duration-700"
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        {/* Front Side */}
        <div
          className={`absolute inset-0 w-full h-full rounded-xl shadow-lg overflow-hidden ${!imageUrl ? 'bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 border-2 border-amber-300' : ''}`}
          style={{ backfaceVisibility: 'hidden' }}
        >
          {imageUrl ? (
            <>
              <img src={imageUrl} alt={certificate.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                <div className="flex items-center space-x-2 mb-1">
                  <Award className="w-4 h-4 text-amber-300" />
                  <span className="font-bold text-sm sm:text-base">{certificate.title}</span>
                  {certificate.verified && (
                    <span className="px-2 py-0.5 bg-green-500/80 text-white text-xs font-semibold rounded-full">✓</span>
                  )}
                </div>
                {certificate.issuer && (
                  <p className="text-xs text-white/80">Issued by: {certificate.issuer}</p>
                )}
                <p className="text-xs text-white/70 mt-2 animate-pulse">Tap to see details →</p>
              </div>
            </>
          ) : (
            <div className="relative z-10 flex flex-col items-center justify-center h-full text-center p-4">
              <div className="absolute inset-2 border-2 border-amber-400 rounded-lg opacity-20"></div>
              <div className="p-3 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full mb-3 shadow-lg">
                <Award className="w-8 h-8 text-white" />
              </div>
              <span className="font-bold text-gray-900 text-base sm:text-lg mb-1">{certificate.title}</span>
              {certificate.verified && (
                <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-semibold rounded-full mb-1">✓ Verified</span>
              )}
              {certificate.issuer && (
                <p className="text-xs text-gray-600">Issued by: {certificate.issuer}</p>
              )}
              <p className="text-xs text-amber-600 mt-2 animate-pulse">Tap to see details →</p>
            </div>
          )}
        </div>

        {/* Back Side */}
        <div
          className="absolute inset-0 w-full h-full p-5 bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 border-2 border-amber-300 rounded-xl shadow-lg"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center space-x-2 mb-2">
              <div className="p-2 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full">
                <Award className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-gray-900 text-sm sm:text-base">{certificate.title}</span>
              {certificate.verified && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">✓</span>
              )}
            </div>
            {certificate.description && (
              <p className="text-sm text-gray-600 mb-2 flex-1">{certificate.description}</p>
            )}
            <div className="space-y-1 border-t border-amber-200 pt-2 text-xs text-gray-500">
              {certificate.issuer && (
                <div className="flex items-center gap-1">
                  <span className="font-medium text-amber-700">Issued by:</span>
                  <span>{certificate.issuer}</span>
                </div>
              )}
              {certificate.year && (
                <span>📅 {certificate.year}</span>
              )}
            </div>
            <p className="text-xs text-amber-600 text-center mt-2 animate-pulse">Tap to flip back ←</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState<any[]>([]);
  const [stories, setStories] = useState(mockStories);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(false);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingPost, setEditingPost] = useState<any>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState<string | null>(null);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showReportModal, setShowReportModal] = useState<{
    isOpen: boolean;
    postId: string;
    postContent: string;
    authorName: string;
  }>({
    isOpen: false,
    postId: '',
    postContent: '',
    authorName: ''
  });
  const [showComments, setShowComments] = useState<string | null>(null);
  const [showStoryViewer, setShowStoryViewer] = useState(false);
  const [showStoryCreator, setShowStoryCreator] = useState(false);
  const [showShareModal, setShowShareModal] = useState<string | null>(null);
  const [currentStoryGroup, setCurrentStoryGroup] = useState<StoryGroup[]>([]);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [storiesRefreshTrigger, setStoriesRefreshTrigger] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedMedia, setSelectedMedia] = useState<{ url: string; type: 'image' | 'video'; thumbnail?: string } | null>(null);

  const [newPost, setNewPost] = useState({
    content: '',
    image: null as File | null,
    video: null as File | null,
    location: '',
    achievement: { title: '', description: '', sport: 'chess' },
    certificate: { title: '', description: '', issuer: '' },
    tags: [] as any[], // Store objects { id, name }
    type: 'text' as 'text' | 'achievement' | 'certificate',
    sport: normalizeSportName(user?.sports?.[0] || 'General'), // Default to user's sport
    year: new Date().getFullYear(), // Default to current year
    visibility: 'public' as 'public' | 'friends' | 'community' | 'private', // Post visibility
    hashtags: ''
  });

  const [showLocationInput, setShowLocationInput] = useState(false);
  const [showTagInput, setShowTagInput] = useState(false);
  const [showAchievementInput, setShowAchievementInput] = useState(false);
  const [showCertificateInput, setShowCertificateInput] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [tagSearchResults, setTagSearchResults] = useState<any[]>([]);
  const [isSearchingTags, setIsSearchingTags] = useState(false);

  // Load feed and stories on component mount
  useEffect(() => {
    loadFeed();
    loadStories();
  }, []);

  // Handle location state messages (for coach approval, etc.)
  useEffect(() => {
    const state = location.state as any;
    if (state?.message) {
      toast.success(state.message);
      // Clear the state to prevent showing the message again on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  // Open create post modal when navigated with ?createPost=true (from mobile nav)
  useEffect(() => {
    if (searchParams.get('createPost') === 'true') {
      setShowCreatePost(true);
      // Remove the query param so it doesn't persist on refresh
      searchParams.delete('createPost');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Listen for custom event from layout nav (when already on home page)
  useEffect(() => {
    const handleOpenCreatePost = () => {
      setShowCreatePost(true);
    };
    window.addEventListener('openCreatePost', handleOpenCreatePost);
    return () => window.removeEventListener('openCreatePost', handleOpenCreatePost);
  }, []);

  const handleTagSearch = async (query: string) => {
    setTagSearchQuery(query);
    if (query.length < 2) {
      setTagSearchResults([]);
      return;
    }

    try {
      setIsSearchingTags(true);
      const response = await api.get(`/users/search?q=${query}&limit=5`);
      if (response.data?.success) {
        // Filter out already tagged users and yourself
        const results = (response.data.data.users || []).filter((u: any) => 
          u.id !== user?._id && !newPost.tags.some(t => t.id === u.id)
        );
        setTagSearchResults(results);
      }
    } catch (error) {
      console.error('Failed to search users for tagging:', error);
    } finally {
      setIsSearchingTags(false);
    }
  };

  const addTag = (taggedUser: any) => {
    setNewPost(prev => ({
      ...prev,
      tags: [...prev.tags, { id: taggedUser.id, name: taggedUser.name }]
    }));
    setTagSearchQuery('');
    setTagSearchResults([]);
  };

  const removeTag = (userId: string) => {
    setNewPost(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t.id !== userId)
    }));
  };

  const loadFeed = async () => {
    try {
      setLoading(true);
      // For now, using mock data. Uncomment below for API integration:
      // const response = await api.get('/posts/feed');
      // setPosts(response.data.data.posts);
    } catch (error) {
      console.error('Failed to load feed:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStories = async () => {
    try {
      // For now, using mock data. Uncomment below for API integration:
      // const response = await api.get('/posts/stories');
      // setStories(response.data.data.stories);
    } catch (error) {
      console.error('Failed to load stories:', error);
    }
  };

  // Load posts and stories on component mount
  useEffect(() => {
    loadPosts();
    loadStories();
  }, []);

  // Refresh posts when user follows/unfollows someone
  const refreshFeed = () => {
    loadPosts();
  };

  // Edit post
  const handleEditPost = (post: any) => {
    setEditingPost(post);
    setShowEditModal(true);
  };

  // Delete post
  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      console.log('Deleting post:', postId);
      
      // Optimistic update
      setPosts(prev => prev.filter(post => post.id !== postId));

      // Real API call
      const response = await api.delete(`/posts/${postId}`);
      console.log('Delete response:', response.data);
      
      if (response.data?.success) {
        toast.success('Post deleted successfully');
      }
    } catch (error: any) {
      console.error('Failed to delete post:', error);
      
      // Revert optimistic update on error
      await loadPosts(1, false);
      toast.error('Failed to delete post. Please try again.');
    }
  };

  // Update post
  const handleUpdatePost = async (postId: string, newContent: string) => {
    try {
      console.log('Updating post:', postId, newContent);
      
      // Optimistic update
      setPosts(prev => prev.map(post => 
        post.id === postId ? { ...post, content: newContent } : post
      ));

      // Real API call
      const response = await api.put(`/posts/${postId}`, { content: newContent });
      console.log('Update response:', response.data);
      
      if (response.data?.success) {
        toast.success('Post updated successfully');
        setShowEditModal(false);
        setEditingPost(null);
      }
    } catch (error: any) {
      console.error('Failed to update post:', error);
      
      // Revert optimistic update on error
      await loadPosts(1, false);
      toast.error('Failed to update post. Please try again.');
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'Player': return 'bg-blue-100 text-blue-800';
      case 'Coach': return 'bg-green-100 text-green-800';
      case 'Organization': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-red-100 text-red-800 font-bold';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSportBadgeColor = (sport: string) => {
    const colors = {
      'Chess': 'bg-red-100 text-red-800',
      'Tennis': 'bg-yellow-100 text-yellow-800',
      'Football': 'bg-blue-100 text-blue-800',
      'Cricket': 'bg-green-100 text-green-800'
    };
    return colors[sport as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const formatPinnedTime = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLike = async (postId: string) => {
    try {
      console.log('Toggling like for post:', postId);
      
      // Optimistic update
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            isLiked: !post.isLiked,
            likes: post.isLiked ? post.likes - 1 : post.likes + 1
          };
        }
        return post;
      }));

      // Real API call
      const response = await api.post(`/posts/${postId}/like`);
      console.log('Like response:', response.data);
      
      if (response.data?.success) {
        console.log('Like toggled successfully');
        // Optionally reload posts to sync with server
        // await loadPosts();
      }
    } catch (error: any) {
      console.error('Failed to toggle like:', error);
      console.error('Error details:', error.response?.data);
      
      // Revert optimistic update on error
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            isLiked: !post.isLiked,
            likes: post.isLiked ? post.likes + 1 : post.likes - 1
          };
        }
        return post;
      }));
    }
  };

  const handleComment = (postId: string) => {
    setShowComments(postId);
  };

  const handleReportPost = (post: any) => {
    setShowReportModal({
      isOpen: true,
      postId: post.id,
      postContent: post.content,
      authorName: post.user.name
    });
  };

  const closeReportModal = () => {
    setShowReportModal({
      isOpen: false,
      postId: '',
      postContent: '',
      authorName: ''
    });
  };

  const handleViewStory = (storyGroup: StoryGroup, startIndex = 0) => {
    setCurrentStoryGroup([storyGroup]);
    setCurrentStoryIndex(startIndex);
    setShowStoryViewer(true);
  };

  const handleCreateStory = () => {
    setShowStoryCreator(true);
  };

  const handleStoryCreated = () => {
    console.log('handleStoryCreated called, triggering refresh');
    // Trigger stories refresh by updating the trigger
    setStoriesRefreshTrigger(prev => {
      console.log('Updating refresh trigger from', prev, 'to', prev + 1);
      return prev + 1;
    });
  };

  // Load real posts from API
  const loadPosts = async (page = 1, append = false) => {
    try {
      setPostsLoading(true);
      console.log('Loading posts from API...', { page, append });
      
      const response = await api.get(`/posts/feed?page=${page}&limit=10`);
      console.log('Posts API response:', response.data);
      
      if (response.data?.success) {
        const postsData = response.data.data.posts || [];
        const hasMore = response.data.data.hasMore || false;
        
        console.log('Posts loaded:', postsData.length, 'posts', { hasMore });
        
        if (append) {
          setPosts(prev => [...prev, ...postsData]);
        } else {
          setPosts(postsData);
        }
        
        setHasMorePosts(hasMore);
        setCurrentPage(page);
      } else {
        console.log('Posts API response not successful, using mock data');
        if (!append) setPosts(mockPosts);
      }
    } catch (error) {
      console.error('Failed to load posts:', error);
      console.log('Using mock data as fallback');
      if (!append) setPosts(mockPosts);
    } finally {
      setPostsLoading(false);
      setLoading(false);
    }
  };

  // Load more posts for infinite scroll
  const loadMorePosts = () => {
    if (hasMorePosts && !postsLoading) {
      loadPosts(currentPage + 1, true);
    }
  };

  const handleShare = async (postId: string) => {
    try {
      // Find the post to share
      const postToShare = posts.find(p => p.id === postId);
      if (!postToShare) return;

      // Update share count optimistically
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return { ...post, shares: post.shares + 1 };
        }
        return post;
      }));
      
      // Real API call to increment share count
      const response = await api.post(`/posts/${postId}/share`);
      
      // Update share count with actual value from server
      if (response.data.success) {
        setPosts(prev => prev.map(post => {
          if (post.id === postId) {
            return { ...post, shares: response.data.data.sharesCount };
          }
          return post;
        }));
        
        // Allow multiple shares - no restriction
      }
      
      // Create share content
      const shareData = {
        title: `${postToShare.user.name} shared a post`,
        text: postToShare.content.substring(0, 100) + (postToShare.content.length > 100 ? '...' : ''),
        url: `${window.location.origin}/posts/${postId}`
      };
      
      // Try native sharing first (mobile)
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success('Post shared successfully!');
      } else {
        // Fallback: Show share modal
        setShowShareModal(postId);
      }
    } catch (error) {
      console.error('Failed to share post:', error);
      toast.error('Failed to share post');
      
      // Revert share count on error
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return { ...post, shares: Math.max(0, post.shares - 1) };
        }
        return post;
      }));
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.content.trim()) return;

    try {
      setLoading(true);
      console.log('Creating post:', newPost);
      
      // Upload media first if selected
      let uploadedImages: any[] = [];
      let uploadedVideos: any[] = [];
      
      // Upload image if selected
      if (newPost.image) {
        try {
          // Validate file size (max 5MB)
          const maxSize = 5 * 1024 * 1024; // 5MB
          if (newPost.image.size > maxSize) {
            toast.error('Image size must be less than 5MB. Please compress or choose a smaller image.');
            setLoading(false);
            return;
          }

          // Validate file type
          const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
          if (!allowedTypes.includes(newPost.image.type)) {
            toast.error('Invalid image format. Please use JPEG, PNG, GIF, or WebP.');
            setLoading(false);
            return;
          }

          console.log('Uploading image...');
          setUploadProgress(30); // Show progress
          
          const uploadResponse = await uploadService.uploadImages([newPost.image]);
          console.log('Image upload response:', uploadResponse);
          
          setUploadProgress(80);
          
          // Handle single file upload response
          if (uploadResponse.success && uploadResponse.data) {
            const fileData = uploadResponse.data;
            uploadedImages = [{
              url: fileData.url,
              publicId: fileData.publicId,
              caption: '',
              alt: 'Post image'
            }];
            console.log('Images uploaded successfully:', uploadedImages);
            setUploadProgress(100);
          } else {
            throw new Error(uploadResponse.error?.message || 'Image upload failed');
          }
        } catch (uploadError: any) {
          console.error('Failed to upload image:', uploadError);
          setUploadProgress(0);
          
          // Handle specific error cases
          if (uploadError.response?.status === 413) {
            toast.error('Image is too large. Please use an image smaller than 5MB.');
          } else if (uploadError.response?.status === 400) {
            toast.error(uploadError.response?.data?.error?.message || 'Invalid image format.');
          } else if (uploadError.message?.includes('network') || uploadError.code === 'NETWORK_ERROR') {
            toast.error('Network error. Please check your connection and try again.');
          } else {
            toast.error(uploadError.response?.data?.error?.message || uploadError.message || 'Failed to upload image. Please try again.');
          }
          setLoading(false);
          return;
        }
      }

      // Upload video if selected
      if (newPost.video) {
        try {
          // Validate file size (max 100MB)
          const maxSize = 100 * 1024 * 1024; // 100MB
          if (newPost.video.size > maxSize) {
            toast.error('Video size must be less than 100MB. Please compress or choose a smaller video.');
            setLoading(false);
            return;
          }

          // Validate file type
          const allowedTypes = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
          if (!allowedTypes.includes(newPost.video.type)) {
            toast.error('Invalid video format. Please use MP4, MPEG, MOV, AVI, or WebM.');
            setLoading(false);
            return;
          }

          console.log('Uploading video...');
          setUploadProgress(30);
          
          const uploadResponse = await uploadService.uploadVideo(newPost.video);
          console.log('Video upload response:', uploadResponse);
          
          setUploadProgress(80);
          
          if (uploadResponse.success && uploadResponse.data) {
            uploadedVideos = [{
              url: uploadResponse.data.url,
              thumbnail: uploadResponse.data.thumbnail,
              duration: uploadResponse.data.duration || 0,
              caption: ''
            }];
            console.log('Video uploaded successfully:', uploadedVideos);
            setUploadProgress(100);
          } else {
            throw new Error(uploadResponse.error?.message || 'Video upload failed');
          }
        } catch (uploadError: any) {
          console.error('Failed to upload video:', uploadError);
          setUploadProgress(0);
          
          if (uploadError.response?.status === 413) {
            toast.error('Video is too large. Please use a video smaller than 100MB.');
          } else if (uploadError.response?.status === 400) {
            toast.error(uploadError.response?.data?.error?.message || 'Invalid video format.');
          } else if (uploadError.message?.includes('network') || uploadError.code === 'NETWORK_ERROR') {
            toast.error('Network error. Please check your connection and try again.');
          } else {
            toast.error(uploadError.response?.data?.error?.message || uploadError.message || 'Failed to upload video. Please try again.');
          }
          setLoading(false);
          return;
        }
      }
      
      // Prepare post data for API
      const postData: any = {
        content: newPost.content.trim(),
        type: newPost.type,
        sport: newPost.sport,
        visibility: newPost.visibility,
        ...(uploadedImages.length > 0 && { images: uploadedImages }),
        ...(uploadedVideos.length > 0 && { videos: uploadedVideos }),
        ...(newPost.location && { location: newPost.location }),
        ...(newPost.hashtags && { hashtags: newPost.hashtags.split(' ').filter(h => h.startsWith('#')).map(h => h.replace('#', '')) })
      };

      // Conditionally add achievement/certificate data
      if (newPost.type === 'achievement') {
        postData.achievement = {
          ...newPost.achievement,
          year: newPost.year
        };
      } else if (newPost.type === 'certificate') {
        postData.certificate = {
          ...newPost.certificate,
          year: newPost.year
        };
      }

      if (newPost.tags.length > 0) {
        postData.tags = newPost.tags.map(t => t.id);
      }

      console.log('Sending post data to API:', postData);
      
      // Real API call
      const response = await api.post('/posts', postData);
      console.log('Post creation response:', response.data);

      if (response.data?.success) {
        console.log('Post created successfully!');
        
        // Reload posts to show the new post
        await loadPosts(1, false);
        
        // Reset form and close modal
        setNewPost({ 
          content: '', 
          image: null as File | null, 
          video: null as File | null, 
          location: '', 
          achievement: { title: '', description: '', sport: 'chess' }, 
          certificate: { title: '', description: '', issuer: '' }, 
          tags: [], 
          type: 'text', 
          sport: normalizeSportName(user?.sports?.[0] || 'General'), 
          year: new Date().getFullYear(), 
          visibility: 'public',
          hashtags: ''
        });
        setUploadProgress(0);
        setShowCreatePost(false);
        
        // Show success message
        toast.success('Post created successfully! 🎉');
      } else {
        console.error('Post creation failed - no success flag');
        toast.error('Failed to create post. Please try again.');
      }
    } catch (error: any) {
      console.error('Failed to create post:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.error?.message || 'Failed to create post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const UserAvatar: React.FC<{ user: any; size?: 'sm' | 'md' | 'lg' }> = ({ user, size = 'md' }) => {
    const mainSport = user.sport || (user.sports && user.sports[0]) || 'General';
    const sizeClasses = {
      sm: 'w-10 h-10 text-xs',
      md: 'w-12 h-12 text-sm',
      lg: 'w-16 h-16 text-base'
    };

    return (
      <div className="relative inline-block">
        <div className={`${sizeClasses[size]} rounded-full flex items-center justify-center overflow-hidden border border-gray-100 ${
          user.avatar ? '' : 'bg-blue-500 text-white font-medium'
        }`}>
          {user.avatar ? (
            <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
          ) : (
            getInitials(user.name)
          )}
        </div>
        {mainSport && mainSport !== 'General' && (
          <div className={`absolute -bottom-1 -right-1 bg-white rounded-full shadow-md flex items-center justify-center border-2 border-white z-10 animate-in fade-in zoom-in duration-300 ${
            size === 'sm' ? 'w-6 h-6 text-sm' : 
            size === 'md' ? 'w-7 h-7 text-base' : 
            'w-10 h-10 text-xl'
          }`}>
            {getSportIcon(mainSport)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-2xl mx-auto px-2 sm:px-4 py-2 sm:py-4">
      {/* Coach Approval Status Banner */}
      {user?.role === 'coach' && user?.status === 'pending' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
          <div className="flex items-start sm:items-center">
            <div className="flex-shrink-0">
              <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
            </div>
            <div className="ml-2 sm:ml-3 flex-1">
              <h3 className="text-xs sm:text-sm font-medium text-yellow-800">
                Account Under Review
              </h3>
              <div className="mt-1 text-xs sm:text-sm text-yellow-700">
                <p>
                  Your coach account is being reviewed by our admin team. You'll be notified once approved!
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Post Button */}
      <div className="bg-white p-3 sm:p-4 mb-3 sm:mb-4 rounded-lg shadow-sm border border-gray-200">
        <button
          onClick={() => setShowCreatePost(true)}
          className="w-full flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs sm:text-sm font-medium flex-shrink-0">
            {user ? getInitials(user.name) : 'U'}
          </div>
          <span className="text-gray-500 text-left flex-1 text-sm sm:text-base">What's on your mind?</span>
          <Plus className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
        </button>
      </div>

      {/* Stories Section */}
      <StoriesSection
        onCreateStory={handleCreateStory}
        onViewStory={handleViewStory}
        refreshTrigger={storiesRefreshTrigger}
      />

      {/* Moderation Notifications */}
      <ModerationNotifications />

      {/* Feed Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 mb-3 sm:mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <span className="text-xs sm:text-sm font-medium text-gray-700">Show:</span>
          <div className="flex flex-wrap gap-2">
            <button className="px-2 sm:px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap">
              All Posts
            </button>
            <button className="px-2 sm:px-3 py-1 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap">
              🏆 Achievements
            </button>
            <button className="px-2 sm:px-3 py-1 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap">
              📜 Certificates
            </button>
            <button className="px-2 sm:px-3 py-1 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap">
              📸 Media
            </button>
          </div>
        </div>
      </div>

      {/* Posts Feed */}
      <div className="space-y-4">
        {loading || postsLoading ? (
          <div className="flex justify-center items-center py-8">
            <LoadingSpinner size="md" text="Loading posts..." />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No posts yet. Be the first to share something!</p>
          </div>
        ) : (
          <>
          {posts.map((post) => (
          <div key={post.id} className={`group bg-white rounded-xl sm:rounded-2xl shadow-sm sm:shadow-lg hover:shadow-xl transition-all duration-300 border ${post.isAdminPost ? 'border-red-300 bg-gradient-to-br from-red-50 to-red-100' : post.type === 'achievement' ? 'border-yellow-300' : post.type === 'certificate' ? 'border-amber-300' : 'border-gray-200'} ${post.pinned ? 'ring-2 sm:ring-4 ring-red-400 ring-opacity-60 shadow-red-200' : ''} mb-3 sm:mb-5 overflow-hidden`}>
            {/* Post Header */}
            <div className="p-3 sm:p-5 bg-gradient-to-r from-gray-50 to-white flex items-start justify-between gap-2 border-b border-gray-100">
              <div className="flex items-start sm:items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                <div className="flex-shrink-0 cursor-pointer" onClick={() => navigate(`/profile/${post.author?.id || post.user.id}`)}>
                  <UserAvatar user={post.user} size="sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                    {post.pinned && (
                      <span className="flex items-center space-x-1 px-1.5 sm:px-2 py-0.5 bg-red-100 text-red-800 text-xs font-medium rounded-full whitespace-nowrap">
                        📌 <span className="hidden sm:inline">PINNED</span>
                        {post.pinnedAt && (
                          <span className="ml-1 text-red-700" title={formatDate(post.pinnedAt)}>
                            • {formatPinnedTime(post.pinnedAt)}
                          </span>
                        )}
                      </span>
                    )}
                    <button 
                      onClick={() => navigate(`/profile/${post.author?.id || post.user.id}`)}
                      className="font-semibold text-sm sm:text-base text-gray-900 hover:text-blue-600 transition-colors cursor-pointer truncate"
                    >
                      {post.user.name}
                    </button>
                    <span className={`px-1.5 sm:px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadgeColor(post.user.role)} whitespace-nowrap`}>
                      {post.isAdminPost ? 'ADMIN' : post.user.role}
                    </span>
                    <span className={`px-1.5 sm:px-2 py-0.5 text-xs font-medium rounded-full ${getSportBadgeColor(post.user.sport)} whitespace-nowrap`}>
                      {post.user.sport}
                    </span>
                    {post.location && (
                      <span className="flex items-center space-x-1 text-gray-500 text-[10px] sm:text-xs">
                        <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-400" />
                        <span className="truncate max-w-[100px] sm:max-w-[150px]">{post.location}</span>
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                      {post.timeAgo}
                      {post.isAdminPost && <span className="ml-1 sm:ml-2 text-red-600 font-medium">• Official</span>}
                    </p>
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1 mt-0.5">
                        <span className="text-[10px] sm:text-xs text-gray-400">with</span>
                        {post.tags.map((tag: any) => (
                          <button
                            key={tag.id}
                            onClick={() => navigate(`/profile/${tag.id}`)}
                            className="text-[10px] sm:text-xs font-medium text-blue-500 hover:text-blue-700 hover:underline"
                          >
                            @{tag.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Post Options Dropdown */}
              <div className="relative flex-shrink-0 dropdown-container">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDropdownId(openDropdownId === post.id ? null : post.id);
                  }}
                  className="p-1 sm:p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <MoreHorizontal className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                
                {/* Dropdown Menu */}
                {openDropdownId === post.id && (
                  <div 
                    className="absolute right-0 top-8 sm:top-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[140px] dropdown-container"
                    onClick={(e) => e.stopPropagation()}
                  >
                  {post.user.id === user?._id ? (
                    // Owner options
                    <>
                      <button
                        onClick={() => {
                          setOpenDropdownId(null);
                          setShowAnalytics(post.id);
                        }}
                        className="w-full px-3 sm:px-4 py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-50"
                      >
                        📊 View Analytics
                      </button>
                      <button
                        onClick={() => {
                          setOpenDropdownId(null);
                          handleEditPost(post);
                        }}
                        className="w-full px-3 sm:px-4 py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-50"
                      >
                        ✏️ Edit Post
                      </button>
                      <button
                        onClick={() => {
                          setOpenDropdownId(null);
                          handleDeletePost(post.id);
                        }}
                        className="w-full px-3 sm:px-4 py-2 text-left text-xs sm:text-sm text-red-600 hover:bg-gray-50"
                      >
                        🗑️ Delete Post
                      </button>
                    </>
                  ) : (
                    // Non-owner options
                    <button
                      onClick={() => {
                        setOpenDropdownId(null);
                        handleReportPost(post);
                      }}
                      className="w-full px-3 sm:px-4 py-2 text-left text-xs sm:text-sm text-red-600 hover:bg-gray-50"
                    >
                      🚩 Report Post
                    </button>
                  )}
                  </div>
                )}
              </div>
            </div>

            {/* Achievement / Certificate Banner */}
            {post.type === 'achievement' && (
              <div className="px-3 sm:px-5 pt-2 sm:pt-3 pb-1">
                <div className="flex items-center space-x-2 px-2.5 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-full w-fit">
                  <Trophy className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-yellow-600 flex-shrink-0" />
                  <span className="text-[10px] sm:text-xs font-bold text-yellow-700 uppercase tracking-wide">Achievement</span>
                </div>
              </div>
            )}
            {post.type === 'certificate' && (
              <div className="px-3 sm:px-5 pt-2 sm:pt-3 pb-1">
                <div className="flex items-center space-x-2 px-2.5 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-full w-fit">
                  <Award className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-600 flex-shrink-0" />
                  <span className="text-[10px] sm:text-xs font-bold text-amber-700 uppercase tracking-wide">Certificate</span>
                </div>
              </div>
            )}

            {/* Post Content */}
            <div className="px-3 sm:px-5 py-3 sm:py-4">
              <p className="text-sm sm:text-base text-gray-900 break-words leading-relaxed">{post.content}</p>
            </div>

            {/* Achievement Card with Flip */}
            {post.type === 'achievement' && post.achievement && (
              <div className="mx-3 sm:mx-5 mb-3 sm:mb-4">
                <FeedAchievementCard achievement={post.achievement} image={post.image} />
              </div>
            )}

            {/* Certificate Card with Flip */}
            {post.type === 'certificate' && post.certificate && (
              <div className="mx-3 sm:mx-5 mb-3 sm:mb-4">
                <FeedCertificateCard certificate={post.certificate} image={post.image} />
              </div>
            )}

            {/* Post Image - skip for achievement/certificate posts since image is shown in flip card */}
            {post.image && post.type !== 'achievement' && post.type !== 'certificate' && (
              <div className="px-3 sm:px-5 pb-3 sm:pb-4">
                <div
                  className="relative overflow-hidden rounded-lg sm:rounded-xl cursor-pointer"
                  onClick={() => setSelectedMedia({ url: post.image, type: 'image' })}
                >
                  <img
                    src={post.image}
                    alt="Post content"
                    className="w-full h-48 sm:h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                </div>
              </div>
            )}

            {/* Post Video */}
            {post.video && (
              <div className="px-3 sm:px-5 pb-3 sm:pb-4">
                <div className="relative overflow-hidden rounded-lg sm:rounded-xl">
                  <video
                    src={post.video.url}
                    controls
                    poster={post.video.thumbnail || undefined}
                    className="w-full h-48 sm:h-64 md:h-80 object-cover rounded-lg sm:rounded-xl"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            )}

            {/* Post Stats */}
            <div className="px-3 sm:px-5 py-2 sm:py-3 bg-gray-50 border-t border-gray-100">
              <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 font-medium">
                <span className="hover:text-blue-600 transition-colors cursor-pointer">{post.likes} {post.likes === 1 ? 'like' : 'likes'}</span>
                <div className="flex space-x-4">
                  <span className="hover:text-blue-600 transition-colors cursor-pointer">{post.comments} {post.comments === 1 ? 'comment' : 'comments'}</span>
                  <span className="hover:text-blue-600 transition-colors cursor-pointer">{post.shares} {post.shares === 1 ? 'share' : 'shares'}</span>
                </div>
              </div>
            </div>

            {/* Post Actions */}
            <div className="px-3 sm:px-5 py-2 sm:py-3 border-t border-gray-100 bg-white">
              <div className="flex items-center justify-around gap-1">
                <button
                  onClick={() => handleLike(post.id)}
                  className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-colors flex-1 justify-center ${
                    post.isLiked 
                      ? 'text-red-600 bg-red-50' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${post.isLiked ? 'fill-current' : ''}`} />
                  <span className="text-xs sm:text-sm font-medium hidden sm:inline">Like</span>
                </button>
                <button 
                  onClick={() => handleComment(post.id)}
                  className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors flex-1 justify-center"
                >
                  <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-xs sm:text-sm font-medium hidden sm:inline">Comment</span>
                </button>
                <button 
                  onClick={() => handleShare(post.id)}
                  className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors flex-1 justify-center"
                >
                  <Share className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-xs sm:text-sm font-medium hidden sm:inline">Share</span>
                </button>
              </div>
            </div>
          </div>
          ))}
          </>
        )}
        
        {/* Load More Button */}
        {hasMorePosts && posts.length > 0 && (
          <div className="flex justify-center py-4 sm:py-6">
            <button
              onClick={loadMorePosts}
              disabled={postsLoading}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-lg text-sm sm:text-base font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {postsLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  <span className="hidden sm:inline">Loading...</span>
                  <span className="sm:hidden">Loading</span>
                </>
              ) : (
                'Load More Posts'
              )}
            </button>
          </div>
        )}
      </div>

      {/* Create Post Modal */}
      {showCreatePost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:w-full sm:max-w-lg mx-0 sm:mx-4 rounded-t-2xl sm:rounded-lg max-h-[95vh] sm:max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Create Post</h2>
              <button
                onClick={() => setShowCreatePost(false)}
                className="p-1 sm:p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 p-4 sm:p-6 space-y-4 overflow-y-auto">
              {/* User Info */}
              <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs sm:text-sm font-medium flex-shrink-0">
                  {user ? getInitials(user.name) : 'U'}
                </div>
                <div>
                  <p className="font-medium text-sm sm:text-base text-gray-900">{user?.name || 'User'}</p>
                    <p className="text-xs sm:text-sm text-gray-500 capitalize">
                      {newPost.visibility === 'public' ? 'Public post' : 
                       newPost.visibility === 'friends' ? 'Friends only' :
                       newPost.visibility === 'community' ? 'Community only' :
                       'Private post'}
                    </p>
                </div>
                </div>
                {/* Visibility Selector */}
                <select
                  value={newPost.visibility}
                  onChange={(e) => setNewPost(prev => ({ ...prev, visibility: e.target.value as any }))}
                  className="text-xs sm:text-sm px-2 sm:px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="public">🌐 Public</option>
                  <option value="friends">👥 Friends</option>
                  <option value="community">🏘️ Community</option>
                  <option value="private">🔒 Private</option>
                </select>
              </div>

              {/* Post Type Selection */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setNewPost(prev => ({ ...prev, type: 'text' }))}
                  className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap ${
                    newPost.type === 'text' 
                      ? 'bg-blue-100 text-blue-600' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Post
                </button>
                <button
                  onClick={() => {
                    setNewPost(prev => ({ ...prev, type: 'achievement' }));
                    setShowAchievementInput(true);
                  }}
                  className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap ${
                    newPost.type === 'achievement' 
                      ? 'bg-yellow-100 text-yellow-600' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  🏆 Achievement
                </button>
                <button
                  onClick={() => {
                    setNewPost(prev => ({ ...prev, type: 'certificate' }));
                    setShowCertificateInput(true);
                  }}
                  className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap ${
                    newPost.type === 'certificate' 
                      ? 'bg-green-100 text-green-600' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  📜 Certificate
                </button>
              </div>

              {/* Achievement Details */}
              {newPost.type === 'achievement' && showAchievementInput && (
                <div className="p-3 sm:p-4 bg-yellow-50 rounded-lg space-y-3 border border-yellow-100">
                  <div className="flex justify-between items-center">
                    <h3 className="text-sm font-semibold text-yellow-800 flex items-center">
                      <Trophy className="w-4 h-4 mr-2" /> Achievement Details
                    </h3>
                    <button onClick={() => setShowAchievementInput(false)} className="text-yellow-600 hover:text-yellow-800">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Achievement Title (e.g. Winner of State Tournament)"
                    value={newPost.achievement.title}
                    onChange={(e) => setNewPost(prev => ({ ...prev, achievement: { ...prev.achievement, title: e.target.value } }))}
                    className="w-full p-2 text-sm border border-yellow-200 rounded focus:ring-1 focus:ring-yellow-500 bg-white"
                  />
                  <textarea
                    placeholder="Short description..."
                    value={newPost.achievement.description}
                    onChange={(e) => setNewPost(prev => ({ ...prev, achievement: { ...prev.achievement, description: e.target.value } }))}
                    className="w-full p-2 text-sm border border-yellow-200 rounded focus:ring-1 focus:ring-yellow-500 bg-white min-h-[60px]"
                  />
                </div>
              )}

              {/* Certificate Details */}
              {newPost.type === 'certificate' && showCertificateInput && (
                <div className="p-3 sm:p-4 bg-green-50 rounded-lg space-y-3 border border-green-100">
                  <div className="flex justify-between items-center">
                    <h3 className="text-sm font-semibold text-green-800 flex items-center">
                      <Award className="w-4 h-4 mr-2" /> Certificate Details
                    </h3>
                    <button onClick={() => setShowCertificateInput(false)} className="text-green-600 hover:text-green-800">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Certificate Title (e.g. Certified Football Coach)"
                    value={newPost.certificate.title}
                    onChange={(e) => setNewPost(prev => ({ ...prev, certificate: { ...prev.certificate, title: e.target.value } }))}
                    className="w-full p-2 text-sm border border-green-200 rounded focus:ring-1 focus:ring-green-500 bg-white"
                  />
                  <input
                    type="text"
                    placeholder="Issuing Organization"
                    value={newPost.certificate.issuer}
                    onChange={(e) => setNewPost(prev => ({ ...prev, certificate: { ...prev.certificate, issuer: e.target.value } }))}
                    className="w-full p-2 text-sm border border-green-200 rounded focus:ring-1 focus:ring-green-500 bg-white"
                  />
                </div>
              )}

              {/* Location Input */}
              {showLocationInput && (
                <div className="flex items-center space-x-2 bg-blue-50 p-2 rounded-lg border border-blue-100">
                  <MapPin className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Add location..."
                    value={newPost.location}
                    onChange={(e) => setNewPost(prev => ({ ...prev, location: e.target.value }))}
                    className="flex-1 bg-transparent border-none text-sm focus:ring-0 placeholder-blue-300"
                    autoFocus
                  />
                  <button onClick={() => { setNewPost(prev => ({ ...prev, location: '' })); setShowLocationInput(false); }} className="text-blue-400 hover:text-blue-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Sport Category Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Sport Category</label>
                <select
                  value={newPost.sport}
                  onChange={(e) => setNewPost(prev => ({ ...prev, sport: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="General">General</option>
                  <option value="Football">Football</option>
                  <option value="Cricket">Cricket</option>
                  <option value="Tennis">Tennis</option>
                  <option value="Chess">Chess</option>
                  <option value="Basketball">Basketball</option>
                  <option value="Swimming">Swimming</option>
                  <option value="Badminton">Badminton</option>
                  <option value="Athletics">Athletics</option>
                  <option value="Volleyball">Volleyball</option>
                  <option value="Hockey">Hockey</option>
                  <option value="Boxing">Boxing</option>
                  <option value="Wrestling">Wrestling</option>
                  <option value="Gymnastics">Gymnastics</option>
                  <option value="Cycling">Cycling</option>
                </select>
              </div>

              {/* Image Preview - Show first if image is selected */}
              {newPost.image && (
                <div className="relative">
                  <img
                    src={URL.createObjectURL(newPost.image)}
                    alt="Preview"
                    className="w-full h-40 sm:h-48 object-cover rounded-lg"
                  />
                  <button
                    onClick={() => setNewPost(prev => ({ ...prev, image: null }))}
                    className="absolute top-2 right-2 p-1.5 sm:p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70"
                  >
                    <X className="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                </div>
              )}

              {/* Video Preview - Show if video is selected */}
              {newPost.video && (
                <div className="relative">
                  <video
                    src={URL.createObjectURL(newPost.video)}
                    controls
                    className="w-full h-40 sm:h-48 object-cover rounded-lg"
                  />
                  <button
                    onClick={() => setNewPost(prev => ({ ...prev, video: null }))}
                    className="absolute top-2 right-2 p-1.5 sm:p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70"
                  >
                    <X className="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                </div>
              )}

              {/* Post Content */}
              <textarea
                value={newPost.content}
                onChange={(e) => setNewPost(prev => ({ ...prev, content: e.target.value }))}
                placeholder="What's on your mind?"
                className="w-full h-24 sm:h-32 p-3 text-sm sm:text-base border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />

              {/* Tag People Input */}
              {showTagInput && (
                <div className="space-y-2 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                  <div className="flex justify-between items-center">
                    <label className="text-xs font-semibold text-indigo-800">Tag People</label>
                    <button onClick={() => setShowTagInput(false)} className="text-indigo-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Selected Tags */}
                  <div className="flex flex-wrap gap-2 mb-2">
                    {newPost.tags.map(tag => (
                      <span key={tag.id} className="flex items-center space-x-1 px-2 py-0.5 bg-indigo-600 text-white rounded-full text-xs">
                        <span>{tag.name}</span>
                        <button onClick={() => removeTag(tag.id)}><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>

                  <div>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search users to tag..."
                        value={tagSearchQuery}
                        onChange={(e) => handleTagSearch(e.target.value)}
                        className="w-full p-2 text-sm border border-indigo-200 rounded focus:ring-1 focus:ring-indigo-500 bg-white pr-8"
                      />
                      {isSearchingTags && (
                        <div className="absolute right-2 top-2">
                          <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </div>

                    {/* Search Results - inline to avoid overflow clipping */}
                    {tagSearchResults.length > 0 && (
                      <div className="mt-1 bg-white border border-indigo-100 rounded-lg shadow max-h-40 overflow-y-auto">
                        {tagSearchResults.map(u => (
                          <button
                            key={u.id}
                            onClick={() => addTag(u)}
                            className="w-full flex items-center space-x-3 p-2 hover:bg-indigo-50 border-b border-gray-50 last:border-0"
                          >
                            <div className="w-6 h-6 rounded-full bg-indigo-200 flex items-center justify-center text-[10px] font-bold overflow-hidden">
                              {u.avatar ? <img src={u.avatar} alt={u.name} className="w-full h-full object-cover" /> : u.name[0]}
                            </div>
                            <span className="text-xs font-medium text-gray-700">{u.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Hashtags Input */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Add hashtags (e.g. #football #winner)"
                  value={newPost.hashtags}
                  onChange={(e) => setNewPost(prev => ({ ...prev, hashtags: e.target.value }))}
                  className="w-full p-2 pl-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="mt-1 flex flex-wrap gap-1">
                  {newPost.hashtags.split(' ').filter(h => h.startsWith('#')).map((h, i) => (
                    <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded text-xs">
                      {h}
                    </span>
                  ))}
                </div>
              </div>

              {/* Year Field - Show for Achievement and Certificate types */}
              {(newPost.type === 'achievement' || newPost.type === 'certificate') && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Year {newPost.type === 'achievement' ? 'Achieved' : 'Issued'} *
                  </label>
                  <select
                    value={newPost.year}
                    onChange={(e) => setNewPost(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {Array.from({ length: new Date().getFullYear() - 1900 + 1 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              {/* Post Options */}
              <div className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg">
                <span className="text-xs sm:text-sm font-medium text-gray-700">Add to your post</span>
                <div className="flex items-center space-x-1 sm:space-x-3">
                  <button 
                    onClick={() => setShowLocationInput(!showLocationInput)}
                    className={`p-1.5 sm:p-2 rounded-full transition-colors ${showLocationInput ? 'bg-red-100 text-red-600' : 'text-red-600 hover:bg-red-50'}`}
                  >
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <button 
                    onClick={() => setShowTagInput(!showTagInput)}
                    className={`p-1.5 sm:p-2 rounded-full transition-colors ${showTagInput ? 'bg-indigo-100 text-indigo-600' : 'text-indigo-600 hover:bg-indigo-50'}`}
                  >
                    <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <label className="cursor-pointer p-1.5 sm:p-2 text-green-600 hover:bg-green-50 rounded-full">
                    <Image className="w-4 h-4 sm:w-5 sm:h-5" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // Clear video if image is selected
                          setNewPost(prev => ({ ...prev, image: file, video: null }));
                        }
                        e.target.value = ''; // Reset input
                      }}
                    />
                  </label>
                  <label className="cursor-pointer p-1.5 sm:p-2 text-purple-600 hover:bg-purple-50 rounded-full">
                    <Video className="w-4 h-4 sm:w-5 sm:h-5" />
                    <input
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // Clear image if video is selected
                          setNewPost(prev => ({ ...prev, video: file, image: null }));
                        }
                        e.target.value = ''; // Reset input
                      }}
                    />
                  </label>
                  <button 
                    onClick={() => {
                      setNewPost(prev => ({ ...prev, type: 'achievement' }));
                      setShowAchievementInput(true);
                      setShowCertificateInput(false);
                    }}
                    className={`p-1.5 sm:p-2 rounded-full transition-colors ${newPost.type === 'achievement' ? 'bg-yellow-100 text-yellow-600' : 'text-yellow-600 hover:bg-yellow-50'}`}
                  >
                    <Trophy className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <button 
                    onClick={() => {
                      setNewPost(prev => ({ ...prev, type: 'certificate' }));
                      setShowCertificateInput(true);
                      setShowAchievementInput(false);
                    }}
                    className={`p-1.5 sm:p-2 rounded-full transition-colors ${newPost.type === 'certificate' ? 'bg-purple-100 text-purple-600' : 'text-purple-600 hover:bg-purple-50'}`}
                  >
                    <Award className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={handleCreatePost}
                disabled={!newPost.content.trim() || loading}
                className="w-full py-2.5 sm:py-3 bg-blue-500 text-white rounded-lg text-sm sm:text-base font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{uploadProgress > 0 ? `Uploading ${uploadProgress}%...` : 'Posting...'}</span>
                  </div>
                ) : 'Post'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comments Section */}
      {showComments && (
        <CommentsSection
          postId={showComments}
          isOpen={!!showComments}
          onClose={() => setShowComments(null)}
        />
      )}

      {/* Story Viewer */}
      {showStoryViewer && (
        <StoryViewer
          storyGroups={currentStoryGroup}
          initialGroupIndex={0}
          initialStoryIndex={currentStoryIndex}
          onClose={() => setShowStoryViewer(false)}
        />
      )}

      {/* Story Creator */}
      <StoryCreator
        isOpen={showStoryCreator}
        onClose={() => setShowStoryCreator(false)}
        onStoryCreated={handleStoryCreated}
      />

      {/* Edit Post Modal */}
      {showEditModal && editingPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg max-w-lg w-full p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Edit Post</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-1 sm:p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
            
            <textarea
              value={editingPost.content}
              onChange={(e) => setEditingPost((prev: any) => ({ ...prev, content: e.target.value }))}
              className="w-full h-24 sm:h-32 p-3 text-sm sm:text-base border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="What's on your mind?"
            />
            
            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-4">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-sm sm:text-base text-gray-600 hover:text-gray-800 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdatePost(editingPost.id, editingPost.content)}
                disabled={!editingPost.content.trim()}
                className="px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update Post
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post Analytics Modal */}
      {showAnalytics && (
        <PostAnalytics
          postId={showAnalytics}
          isOpen={!!showAnalytics}
          onClose={() => setShowAnalytics(null)}
        />
      )}

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          postId={showShareModal}
          post={posts.find(p => p.id === showShareModal)}
          onClose={() => setShowShareModal(null)}
        />
      )}

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal.isOpen}
        onClose={closeReportModal}
        postId={showReportModal.postId}
        postContent={showReportModal.postContent}
        authorName={showReportModal.authorName}
      />

      {/* Media Lightbox */}
      {selectedMedia && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-[100] flex items-center justify-center p-4"
          onClick={() => setSelectedMedia(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            onClick={() => setSelectedMedia(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-4xl max-h-full w-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            {selectedMedia.type === 'image' ? (
              <img
                src={selectedMedia.url}
                alt="Full size"
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
              />
            ) : (
              <video
                src={selectedMedia.url}
                controls
                autoPlay
                poster={selectedMedia.thumbnail}
                className="max-w-full max-h-[90vh] rounded-lg"
              >
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;